import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';

const features = [
  {
    icon: '📄',
    title: 'Large Document Support',
    desc: 'Process research papers, legal briefs, books, and technical manuals — up to 1,000+ pages with no size limits.',
  },
  {
    icon: '⚡',
    title: 'Instant Summaries',
    desc: 'Our AI reads your document section-by-section and builds a coherent, comprehensive summary in minutes.',
  },
  {
    icon: '💬',
    title: 'Chat with your PDF',
    desc: 'Ask follow-up questions about the content. Get precise answers grounded in your document.',
  },
  {
    icon: '🗂️',
    title: 'Document History',
    desc: 'All your processed documents and summaries are saved. Revisit and re-read anytime.',
  },
  {
    icon: '🔒',
    title: 'Secure & Private',
    desc: 'Documents are stored in your private Firebase account. Only you can access your files.',
  },
  {
    icon: '💳',
    title: 'Pay as You Go',
    desc: 'No subscription lock-in. Buy credits when you need them — starting at just $3 per summary.',
  },
];

const stats = [
  { value: '1,000+', label: 'Pages per document' },
  { value: '< 2 min', label: 'Average processing time' },
  { value: '99%', label: 'Extraction accuracy' },
  { value: '3 free', label: 'Credits on sign-up' },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      <Navigation />

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="hero-gradient text-white relative overflow-hidden">
        {/* grid overlay */}
        <div className="absolute inset-0 pointer-events-none opacity-10"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.07) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
        {/* glow orbs */}
        <div className="absolute -top-24 -left-24 w-[500px] h-[500px] rounded-full opacity-25 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, #4f46e5 0%, transparent 70%)' }} />
        <div className="absolute -bottom-32 right-0 w-[420px] h-[420px] rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)' }} />

        <div className="relative z-10 mx-auto max-w-5xl px-6 py-28 lg:py-36 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 text-sm font-semibold border border-indigo-400/30 bg-indigo-500/10 text-indigo-300">
            🎁 New users get <span className="text-white font-bold">3 free credits</span> instantly
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-tight tracking-tight">
            Summarize any{' '}
            <span className="gradient-text">PDF document</span>
            <br />with AI
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Upload a PDF — even a 1,000-page book — and get a clear, accurate summary in minutes. Then chat with your document to get instant answers.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/home"
              className="inline-flex items-center justify-center gap-2 rounded-xl px-8 py-3.5 text-base font-semibold text-white transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', boxShadow: '0 4px 20px rgba(79,70,229,0.45)' }}
            >
              Get started free
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 rounded-xl px-8 py-3.5 text-base font-semibold text-white border border-white/20 bg-white/5 hover:bg-white/10 transition-all duration-200"
            >
              View pricing
            </Link>
          </div>
        </div>
      </section>

      {/* ── Stats strip ─────────────────────────────────────────────────── */}
      <section className="border-y theme-border" style={{ background: 'var(--bg-secondary)' }}>
        <div className="mx-auto max-w-5xl px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="text-3xl font-extrabold gradient-text">{s.value}</div>
              <div className="mt-1 text-sm theme-text-secondary">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-6 py-24">
        <div className="text-center mb-16">
          <p className="text-sm font-semibold tracking-widest uppercase mb-3" style={{ color: 'var(--accent-primary)' }}>
            What you get
          </p>
          <h2 className="text-3xl sm:text-4xl font-extrabold theme-text-primary">
            Everything you need to understand any document
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f) => (
            <div key={f.title} className="theme-card p-6 group cursor-default">
              <div className="text-3xl mb-4">{f.icon}</div>
              <h3 className="text-base font-bold theme-text-primary mb-2">{f.title}</h3>
              <p className="text-sm theme-text-secondary leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA banner ──────────────────────────────────────────────────── */}
      <section className="hero-gradient text-white">
        <div className="mx-auto max-w-3xl px-6 py-20 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">
            Ready to summarize your first PDF?
          </h2>
          <p className="text-slate-300 mb-8">
            Sign in with Google and use your 3 free credits — no card required.
          </p>
          <Link
            to="/home"
            className="inline-flex items-center gap-2 rounded-xl px-8 py-3.5 text-base font-semibold text-white transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', boxShadow: '0 4px 20px rgba(79,70,229,0.45)' }}
          >
            Start for free
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
}
